import { Card, Typography, Row, Col, Drawer, Button, message } from 'antd';
import { useEffect, useState } from 'react';
import { routesMapper } from '@/constant/routesMapper';
import { storageUtil } from '@/utils/storage/local-storage.util';
import QuickLinkCard from './QuickLinkCard';
import { Plus, ScrollIcon } from 'lucide-react';
import {
  useCreateQuickLinksBulkMutation,
  useDeleteQuickLinkMutation,
  useGetAllQuickLinksQuery,
} from '@/store/apis/quickLinkApi';
import Scrollbars from 'rc-scrollbars';

interface QuickLink {
  id?: string;
  title: string;
  description: string;
  link: string;
  icon?: string;
}

const renderThumb = ({ style, ...props }: any) => {
  const thumbStyle = {
    backgroundColor: '#fff',
    borderRadius: '5px',
    opacity: 0.5,
  };
  return <div style={{ ...style, ...thumbStyle }} {...props} />;
};

const QuickLinks = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [quickLinks, setQuickLinks] = useState<QuickLink[]>([]);
  const [tempLinks, setTempLinks] = useState<QuickLink[]>([]);

  const [createQuickLinksBulk] = useCreateQuickLinksBulkMutation();
  const [deleteQuickLink] = useDeleteQuickLinkMutation();
  const { data: allQuickLinks, refetch: refetchAllQuickLinks } =
    useGetAllQuickLinksQuery();

  useEffect(() => {
    if (allQuickLinks?.data) {
      setQuickLinks(allQuickLinks?.data);
      storageUtil.setItem('quickLinks', allQuickLinks?.data);
    }
  }, [allQuickLinks?.data]);

  const openDrawer = () => {
    setTempLinks([...quickLinks]);
    setIsModalOpen(true);
  };

  const isLinkSelected = (link: string) => {
    return tempLinks.some((item) => item.link === link);
  };

  const toggleLink = (route: {
    title: string;
    description?: string;
    route: string;
    icon?: string;
  }) => {
    const exists = tempLinks.find((item) => item.link === route.route);

    if (exists) {
      setTempLinks(tempLinks.filter((item) => item.link !== route.route));
    } else {
      const existing =
        quickLinks.find((item: QuickLink) => item.link === route.route) ||
        allQuickLinks?.data?.find(
          (item: QuickLink) => item.link === route.route,
        );

      setTempLinks([
        ...tempLinks,
        {
          id: existing?.id,
          title: route.title,
          description: route.description || '',
          link: route.route,
        },
      ]);
    }
  };

  const handleSaveLinks = async () => {
    try {
      const data = await createQuickLinksBulk(tempLinks);
      setQuickLinks(data?.data?.data);
      setIsModalOpen(false);
      refetchAllQuickLinks();
      message.success('Quick links updated');
    } catch (error) {
      console.log(error);
      message.error('Failed to update quick links');
    }
  };

  const handleDeleteLink = async (id: string) => {
    try {
      await deleteQuickLink(id);
      setQuickLinks(quickLinks.filter((link) => link.id !== id));
      refetchAllQuickLinks();
      message.success('Quick link deleted successfully');
    } catch (error) {
      console.log(error);
      message.error('Failed to delete quick link');
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingBottom: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Text style={{ color: '#fff' }}>Quick Links</Typography.Text>
        <div
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={openDrawer}
        >
          <Plus />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {quickLinks.length > 0 ? (
          <Scrollbars
            style={{
              height: '100px',
            }}
            autoHide
            renderThumbHorizontal={renderThumb}
            renderThumbVertical={renderThumb}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
            >
              {quickLinks.map((quickLink: QuickLink) => (
                <QuickLinkCard
                  key={quickLink.id}
                  id={quickLink.id || ''}
                  title={quickLink.title}
                  description={quickLink.description}
                  link={quickLink.link}
                  onDelete={handleDeleteLink}
                />
              ))}
            </div>
          </Scrollbars>
        ) : null}
      </div>

      <Drawer
        width={500}
        title="Add Quick Link"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <Button type="primary" onClick={handleSaveLinks}>
              Add
            </Button>
            <Button type="default" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        {routesMapper.map((group) => (
          <div key={group.title} style={{ marginBottom: '24px' }}>
            <Typography.Title level={5}>{group.title}</Typography.Title>
            <Row gutter={[12, 12]}>
              {group.routes.map((route) => {
                const selected = isLinkSelected(route.route);
                return (
                  <Col span={12} key={route.route}>
                    <Card
                      hoverable
                      onClick={() => toggleLink(route)}
                      size="small"
                      style={{
                        borderColor: selected ? '#1677ff' : '#d9d9d9',
                        backgroundColor: selected ? '#e6f4ff' : '#fafafa',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        height: '100%',
                        boxShadow: selected
                          ? '0 0 4px rgba(22, 119, 255, 0.3)'
                          : undefined,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{
                          display: 'block',
                          marginBottom: 4,
                          fontSize: 12,
                        }}
                      >
                        {route.title}
                      </Typography.Text>
                      <Typography.Paragraph
                        style={{
                          margin: 0,
                          fontSize: 10,
                          color: '#666',
                        }}
                        ellipsis={{ rows: 2 }}
                      >
                        {route.description}
                      </Typography.Paragraph>
                      <Typography.Text
                        style={{
                          fontSize: 11,
                          color: '#1677ff',
                          wordBreak: 'break-all',
                        }}
                      >
                        {route.route}
                      </Typography.Text>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}
      </Drawer>
    </div>
  );
};

export default QuickLinks;
